import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";

const CATEGORIES = ["travel", "finance", "recharge", "food-delivery", "education", "services", "other"];

export default function DealsAdmin() {
  const qc = useQueryClient();
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [active, setActive] = useState("");
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-deals", category, q, active],
    queryFn: async () => (await api.get("/admin/deals", { params: { category, q, active } })).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-deals"] });

  const toggle = async (deal) => {
    setError("");
    try {
      await api.patch(`/admin/deals/${deal.slug}`, { active: !deal.active });
      await refresh();
    } catch (err) {
      setError(apiError(err));
    }
  };

  const remove = async (deal) => {
    setError("");
    try {
      await api.delete(`/admin/deals/${deal.slug}`);
      await refresh();
    } catch (err) {
      setError(apiError(err));
    }
  };

  const save = async (event) => {
    event.preventDefault();
    setError("");
    try {
      await api.patch(`/admin/deals/${editing.slug}`, {
        name: editing.name,
        category: editing.category,
        offer_text: editing.offer_text,
        description: editing.description,
        coupon_code: editing.coupon_code,
        expires_at: editing.expires_at,
        affiliate_url: editing.affiliate_url,
        landing_url: editing.landing_url,
        logo_url: editing.logo_url,
      });
      setEditing(null);
      await refresh();
    } catch (err) {
      setError(apiError(err));
    }
  };

  const deals = data?.deals || [];

  return (
    <section className="mb-10 border border-aura-border rounded-2xl p-5 bg-aura-surface" data-testid="admin-deals">
      <div className="font-serif text-xl tracking-tight">Deals</div>
      <p className="mt-1 text-xs text-aura-muted">Brand offers imported from INRDeals. A deal not sent again for {data?.stale_days ?? 14} days is hidden.</p>
      <div className="my-4 flex flex-wrap gap-2">
        <input value={q} onChange={(event) => setQ(event.target.value)} placeholder="Search" className="border border-aura-border bg-transparent px-3 py-1.5 text-sm" data-testid="admin-deals-search" />
        <select value={category} onChange={(event) => setCategory(event.target.value)} className="border border-aura-border bg-transparent px-3 py-1.5 text-sm" data-testid="admin-deals-category">
          <option value="">All categories</option>
          {CATEGORIES.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
        <select value={active} onChange={(event) => setActive(event.target.value)} className="border border-aura-border bg-transparent px-3 py-1.5 text-sm" data-testid="admin-deals-active">
          <option value="">Active and hidden</option>
          <option value="true">Active</option>
          <option value="false">Hidden</option>
        </select>
      </div>
      {error ? <p className="mb-3 text-sm text-aura-accent">{error}</p> : null}
      {editing ? (
        <form onSubmit={save} className="mb-4 grid gap-2 border border-aura-border p-3 text-sm" data-testid="admin-deal-edit">
          <input value={editing.name} onChange={(event) => setEditing({ ...editing, name: event.target.value })} className="border border-aura-border bg-transparent px-2 py-1" />
          <input value={editing.offer_text} onChange={(event) => setEditing({ ...editing, offer_text: event.target.value })} className="border border-aura-border bg-transparent px-2 py-1" />
          <input value={editing.coupon_code || ""} onChange={(event) => setEditing({ ...editing, coupon_code: event.target.value })} placeholder="Coupon" className="border border-aura-border bg-transparent px-2 py-1" />
          <input value={editing.expires_at || ""} onChange={(event) => setEditing({ ...editing, expires_at: event.target.value })} placeholder="Expires" className="border border-aura-border bg-transparent px-2 py-1" />
          <input value={editing.affiliate_url || ""} onChange={(event) => setEditing({ ...editing, affiliate_url: event.target.value })} className="border border-aura-border bg-transparent px-2 py-1" />
          <div className="flex gap-2">
            <button type="submit" className="btn-accent rounded-full px-4 py-1.5 text-xs">Save</button>
            <button type="button" onClick={() => setEditing(null)} className="rounded-full border border-aura-border px-4 py-1.5 text-xs">Cancel</button>
          </div>
        </form>
      ) : null}
      <div className="divide-y divide-aura-border border border-aura-border">
        {isLoading ? <div className="px-4 py-4 text-sm text-aura-muted">Loading…</div> : null}
        {!isLoading && deals.length === 0 ? <div className="px-4 py-4 text-sm text-aura-muted">No deals yet.</div> : null}
        {deals.map((deal) => (
          <div key={deal.slug} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`admin-deal-${deal.slug}`}>
            <div>
              <div className="text-sm font-medium">{deal.name}</div>
              <div className="text-xs text-aura-muted">{deal.category} · {deal.offer_text} · {deal.active ? "showing" : "hidden"}{deal.coupon_code ? ` · ${deal.coupon_code}` : ""}</div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => toggle(deal)} className="rounded-full border border-aura-border px-3 py-1 text-xs" data-testid={`admin-deal-toggle-${deal.slug}`}>
                {deal.active ? "Hide" : "Show"}
              </button>
              <button type="button" onClick={() => setEditing({ ...deal })} className="rounded-full border border-aura-border px-3 py-1 text-xs">Edit</button>
              <button type="button" onClick={() => remove(deal)} className="rounded-full border border-aura-border px-3 py-1 text-xs" data-testid={`admin-deal-delete-${deal.slug}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
